import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useCatalog } from '../context/CatalogContext'
import { subscribeStaff } from '../services/products'
import './SetupProgress.css'

/**
 * Aktivasyondan sonra patronun önündeki yol, her sayfanın üstünde.
 *
 * Kurulum sihirbazı kurumu açıp bırakıyordu: patron "Menüde aktif ürün yok"
 * yazan boş bir kasaya düşüyor ve sırada ne olduğunu söyleyen bir şey yoktu.
 * Ürünleri de çoğu zaman kurulum anında değil ertesi gün giriyor, yani
 * sihirbazı uzatmak çözüm değil; yol, işi yapacağı ekranların üstünde duruyor.
 *
 * Adımlar veriden okunuyor, işaretlenmiyor: kategori açıldığı an o adım
 * tamam. Menüyü CSV'den yükleyen biri iki adımı birden geçiyor ve bunu kimseye
 * söylemesi gerekmiyor. Her şey bitince çubuk kendiliğinden kalkıyor.
 *
 * Masa ve personel atlanabiliyor: yalnızca gel-al yapan bir büfenin masası,
 * tek başına çalışan birinin personeli yok. Atlanan adım o cihazda
 * hatırlanıyor, kurum geneline yazmaya değecek bir bilgi değil.
 */
type StepId = 'account' | 'categories' | 'products' | 'tables' | 'staff'

interface Step {
  id: StepId
  label: string
  hint: string
  to?: string
  optional?: boolean
}

const STEPS: Step[] = [
  { id: 'account', label: 'Hesap açıldı', hint: '' },
  {
    id: 'categories',
    label: 'Kategori ekle',
    hint: 'Menünün bölümleri: Çorbalar, Ana Yemekler, İçecekler…',
    to: '/menu',
  },
  {
    id: 'products',
    label: 'Ürün ekle',
    hint: 'Adı ve fiyatı yeterli. Kalabalık bir menüyü CSV ile toplu yükleyebilirsiniz.',
    to: '/menu',
  },
  {
    id: 'tables',
    label: 'Masaları kur',
    hint: 'Salonlarınızı ve masalarınızı ekleyin.',
    to: '/masalar',
    optional: true,
  },
  {
    id: 'staff',
    label: 'Personel ekle',
    hint: 'Kasiyer ve garsonlara kendi kullanıcı adı ve PIN’lerini verin.',
    to: '/ayarlar?bolum=staff',
    optional: true,
  },
]

const skipKey = (tenantId: string) => `adissoo.setupSkip.${tenantId}`

function readSkipped(tenantId: string): StepId[] {
  try {
    const raw = localStorage.getItem(skipKey(tenantId))
    return raw ? (JSON.parse(raw) as StepId[]) : []
  } catch {
    return []
  }
}

export function SetupProgress() {
  const { isOwner, tenantId } = useAuth()
  const { categories, products, activeTables, loading } = useCatalog()
  const { tenant } = useParams()

  // null: henüz gelmedi. Gelmeden "personel yok" deyip adımı açık göstermek,
  // her açılışta çubuğun bir an görünüp kaybolması demek.
  const [staffCount, setStaffCount] = useState<number | null>(null)
  useEffect(() => {
    if (!isOwner || !tenantId) return
    return subscribeStaff(
      tenantId,
      (staff) => setStaffCount(staff.length),
      () => setStaffCount(null),
    )
  }, [isOwner, tenantId])

  const [skipped, setSkipped] = useState<StepId[]>(() =>
    tenantId ? readSkipped(tenantId) : [],
  )
  const [open, setOpen] = useState(false)

  if (!isOwner || !tenantId || loading || staffCount === null) return null

  const done: Record<StepId, boolean> = {
    account: true,
    categories: categories.length > 0,
    products: products.length > 0,
    tables: activeTables.length > 0,
    // Patronun kendisi de bu listede.
    staff: staffCount > 1,
  }
  const isDone = (s: Step) => done[s.id] || skipped.includes(s.id)

  const finished = STEPS.filter(isDone).length
  if (finished === STEPS.length) return null

  const next = STEPS.find((s) => !isDone(s))!
  const percent = Math.round((finished / STEPS.length) * 100)
  const href = (to: string) => `/${tenant}${to}`

  const skip = (id: StepId) => {
    const list = [...skipped, id]
    setSkipped(list)
    try {
      localStorage.setItem(skipKey(tenantId), JSON.stringify(list))
    } catch {
      // Depolama yoksa atlanan adım yenileyince geri gelir, o kadar.
    }
  }

  return (
    <section className={`setup ${open ? 'is-open' : ''}`} aria-label="Kurulum">
      <button
        type="button"
        className="setup__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="setup__title">
          Kurulum <b>{finished}/{STEPS.length}</b>
        </span>
        <span
          className="setup__bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <i style={{ width: `${percent}%` }} />
        </span>
        <span className="setup__next">
          Sıradaki: <b>{next.label}</b>
        </span>
        <span className="setup__chev" aria-hidden="true" />
      </button>

      {open && (
        <ol className="setup__steps">
          {STEPS.map((s, i) => {
            const ok = isDone(s)
            const isNext = s.id === next.id
            return (
              <li
                key={s.id}
                className={`setup__step ${ok ? 'is-done' : ''} ${isNext ? 'is-next' : ''}`}
              >
                <span className="setup__mark">{ok ? '✓' : i + 1}</span>
                <span className="setup__text">
                  <span className="setup__label">
                    {s.label}
                    {skipped.includes(s.id) && !done[s.id] && (
                      <span className="setup__skipped">, atlandı</span>
                    )}
                  </span>
                  {!ok && s.hint && <span className="setup__hint">{s.hint}</span>}
                </span>
                {!ok && (
                  <span className="setup__actions">
                    {s.optional && (
                      <button
                        type="button"
                        className="setup__skip"
                        onClick={() => skip(s.id)}
                      >
                        Atla
                      </button>
                    )}
                    {s.to && (
                      <Link
                        to={href(s.to)}
                        className={`setup__go ${isNext ? 'is-primary' : ''}`}
                        onClick={() => setOpen(false)}
                      >
                        Git
                      </Link>
                    )}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
